import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { P3Risk } from "../p3/p3Types";

const IMPACTS = ["low", "medium", "high"] as const;
const PROBS = ["high", "medium", "low"] as const;

const CELL_STYLES: Record<string, string> = {
    "high-high": "bg-destructive/80 text-destructive-foreground",
    "high-medium": "bg-orange-500/80 text-white",
    "high-low": "bg-amber-400/80 text-amber-950",
    "medium-high": "bg-orange-500/70 text-white",
    "medium-medium": "bg-amber-400/70 text-amber-950",
    "medium-low": "bg-emerald-300/80 text-emerald-950",
    "low-high": "bg-amber-300/70 text-amber-950",
    "low-medium": "bg-emerald-300/70 text-emerald-950",
    "low-low": "bg-emerald-200/80 text-emerald-950",
};

const LEVEL_LABELS: Record<string, string> = {
    "high-high": "Intolerable",
    "high-medium": "High",
    "high-low": "Moderate",
    "medium-high": "High",
    "medium-medium": "Moderate",
    "medium-low": "Low",
    "low-high": "Moderate",
    "low-medium": "Low",
    "low-low": "Trivial",
};

type Props = {
    risks: P3Risk[];
    selectedCell: string | null;
    onSelectCell: (cell: string | null) => void;
};

export function RiskHeatmap({ risks, selectedCell, onSelectCell }: Props) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">Risk level matrix</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="mb-2 text-xs text-muted-foreground">
                    Impact → / Probability ↓
                </p>
                <div className="grid grid-cols-4 gap-1 text-center text-[10px]">
                    <div />
                    {IMPACTS.map((i) => (
                        <div key={i} className="font-medium capitalize">
                            {i}
                        </div>
                    ))}
                    {PROBS.map((prob) => (
                        <div key={prob} className="contents">
                            <div className="flex items-center justify-end pr-1 font-medium capitalize">
                                {prob}
                            </div>
                            {IMPACTS.map((imp) => {
                                const key = `${imp}-${prob}`;
                                const inCell = risks.filter(
                                    (r) =>
                                        r.impact === imp &&
                                        r.probability === prob,
                                );
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() =>
                                            onSelectCell(
                                                selectedCell === key
                                                    ? null
                                                    : key,
                                            )
                                        }
                                        className={cn(
                                            "flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-md p-1 transition-all",
                                            CELL_STYLES[key],
                                            selectedCell === key &&
                                                "ring-2 ring-primary ring-offset-1",
                                        )}
                                    >
                                        <span className="text-[9px] font-medium opacity-90">
                                            {LEVEL_LABELS[key]}
                                        </span>
                                        {inCell.map((r) => (
                                            <Badge
                                                key={r.code}
                                                variant="secondary"
                                                className="h-4 px-1 text-[8px] font-mono"
                                            >
                                                {r.code}
                                            </Badge>
                                        ))}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

export function riskMatchesCell(risk: P3Risk, cell: string): boolean {
    const [imp, prob] = cell.split("-");
    return risk.impact === imp && risk.probability === prob;
}
